import React, { useState } from 'react';
import SEO from '../components/SEO';

const PrivacyTermsPage = () => {
  const [activeTab, setActiveTab] = useState('privacy');

  return (
    <div className="pt-24">
      <SEO 
        title="Privacy Policy & Terms | Alpha Consulting LLC"
        description="Alpha Consulting LLC Privacy Policy and Terms of Service. Learn about our data protection practices, service terms, and client commitments."
        keywords="privacy policy, terms of service, data protection, Alpha Consulting LLC legal"
        url="https://thealphaconsult.com/privacy"
      />
      <section className="relative py-20 bg-gradient-to-b from-orange-950 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300/80 font-nunito">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span> Legal
            </span>
            <h1 className="mt-3 text-3xl sm:text-4xl tracking-tight font-nunito font-bold">
              Privacy Policy & Terms of Service
            </h1>
            <p className="mt-3 text-orange-300 font-nunito">
              Last updated: October 28, 2025
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-4 mb-8 border-b border-white/10">
            <button
              onClick={() => setActiveTab('privacy')}
              className={`pb-3 px-4 font-nunito font-medium transition-colors border-b-2 ${
                activeTab === 'privacy'
                  ? 'border-amber-400 text-amber-300'
                  : 'border-transparent text-orange-300 hover:text-amber-300'
              }`}
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setActiveTab('terms')}
              className={`pb-3 px-4 font-nunito font-medium transition-colors border-b-2 ${
                activeTab === 'terms'
                  ? 'border-amber-400 text-amber-300'
                  : 'border-transparent text-orange-300 hover:text-amber-300'
              }`}
            >
              Terms of Service
            </button>
          </div>

          {/* Privacy Policy Content */}
          {activeTab === 'privacy' && (
            <div className="space-y-8">
              {/* Introduction */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Introduction
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  Alpha Consulting LLC ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                  explains how we collect, use, disclose, and safeguard your information when you visit our 
                  website and use our services. Please read this policy carefully to understand our practices 
                  regarding your personal data.
                </p>
              </div>

              {/* Information We Collect */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Information We Collect
                </h2>
                <div className="space-y-4 text-orange-200 font-nunito">
                  <div>
                    <h3 className="text-lg font-semibold text-amber-200 mb-2">Personal Information</h3>
                    <p className="leading-relaxed">
                      We may collect personal information that you voluntarily provide to us when you:
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                      <li>Fill out contact forms or request proposals</li>
                      <li>Subscribe to our newsletters or communications</li>
                      <li>Create an account or engage our services</li>
                      <li>Participate in surveys or feedback requests</li>
                    </ul>
                    <p className="mt-2">
                      This may include: name, email address, phone number, company name, job title, and 
                      project requirements.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-amber-200 mb-2">Automatically Collected Information</h3>
                    <p className="leading-relaxed">
                      When you visit our website, we automatically collect certain information about your device, 
                      including:
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                      <li>IP address and browser type</li>
                      <li>Operating system and device information</li>
                      <li>Pages visited, time spent, and referring URLs</li>
                      <li>Cookies and similar tracking technologies</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* How We Use Your Information */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  How We Use Your Information
                </h2>
                <div className="text-orange-200 font-nunito space-y-2">
                  <p className="leading-relaxed">We use the information we collect to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Provide, operate, and maintain our services</li>
                    <li>Respond to your inquiries and fulfill your requests</li>
                    <li>Send you technical notices, updates, and administrative messages</li>
                    <li>Communicate with you about products, services, and promotional offers</li>
                    <li>Analyze usage patterns and improve our website and services</li>
                    <li>Detect, prevent, and address technical issues and security threats</li>
                    <li>Comply with legal obligations and enforce our terms</li>
                  </ul>
                </div>
              </div>

              {/* Third-Party Services */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Third-Party Services & Data Sharing
                </h2>
                <div className="space-y-4 text-orange-200 font-nunito">
                  <div>
                    <h3 className="text-lg font-semibold text-amber-200 mb-2">Google Services</h3>
                    <p className="leading-relaxed">
                      We use Google services including Google Analytics, Google Fonts, and Google Cloud Platform. 
                      These services may collect information about your use of our website. Google's use of this 
                      information is governed by Google's Privacy Policy. You can opt out of Google Analytics by 
                      installing the Google Analytics Opt-out Browser Add-on.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-amber-200 mb-2">Other Third-Party Tools</h3>
                    <p className="leading-relaxed">
                      We may use additional third-party services for:
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                      <li>Email delivery and marketing automation</li>
                      <li>Customer relationship management (CRM)</li>
                      <li>Payment processing and invoicing</li>
                      <li>Cloud hosting and infrastructure services (AWS, Azure)</li>
                      <li>Analytics and performance monitoring</li>
                    </ul>
                    <p className="mt-2">
                      We ensure these third parties adhere to appropriate data protection standards and only 
                      share information necessary to provide their services.
                    </p>
                  </div>
                </div>
              </div>

              {/* Cookies and Tracking */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Cookies and Tracking Technologies
                </h2>
                <div className="text-orange-200 font-nunito space-y-3">
                  <p className="leading-relaxed">
                    We use cookies and similar tracking technologies to track activity on our website and store 
                    certain information. You can instruct your browser to refuse all cookies or to indicate when 
                    a cookie is being sent. However, if you do not accept cookies, you may not be able to use 
                    some portions of our website.
                  </p>
                  <p className="leading-relaxed">
                    Types of cookies we use:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                    <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                    <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                    <li><strong>Marketing Cookies:</strong> Track your online activity to deliver relevant ads</li>
                  </ul>
                </div>
              </div>

              {/* Data Security */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Data Security
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal 
                  information against unauthorized access, alteration, disclosure, or destruction. These measures 
                  include encryption, access controls, secure server infrastructure, and regular security assessments. 
                  However, no method of transmission over the Internet or electronic storage is 100% secure, and we 
                  cannot guarantee absolute security.
                </p>
              </div>

              {/* Data Retention */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Data Retention
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined 
                  in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. 
                  When we no longer need your information, we will securely delete or anonymize it.
                </p>
              </div>

              {/* Your Rights */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Your Privacy Rights
                </h2>
                <div className="text-orange-200 font-nunito space-y-2">
                  <p className="leading-relaxed">
                    Depending on your location, you may have the following rights regarding your personal information:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                    <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                    <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                    <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                    <li><strong>Objection:</strong> Object to processing of your personal information</li>
                    <li><strong>Restriction:</strong> Request restriction of processing</li>
                    <li><strong>Withdraw Consent:</strong> Withdraw consent where we rely on consent to process</li>
                  </ul>
                  <p className="mt-3">
                    To exercise these rights, please contact us at info@thealphaconsult.com
                  </p>
                </div>
              </div>

              {/* International Data Transfers */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  International Data Transfers
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  Your information may be transferred to and processed in countries other than your country of 
                  residence. These countries may have data protection laws that are different from the laws of 
                  your country. We take appropriate safeguards to ensure that your personal information remains 
                  protected in accordance with this Privacy Policy.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Children's Privacy
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect 
                  personal information from children. If you become aware that a child has provided us with 
                  personal information, please contact us immediately.
                </p>
              </div>

              {/* Changes to Privacy Policy */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Changes to This Privacy Policy
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by 
                  posting the new Privacy Policy on this page and updating the "Last updated" date. You are 
                  advised to review this Privacy Policy periodically for any changes.
                </p>
              </div>

              {/* Contact */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Contact Us
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  If you have any questions about this Privacy Policy or our data practices, please contact us at:
                </p>
                <div className="mt-3 text-orange-200 font-nunito">
                  <p>Email: info@thealphaconsult.com</p>
                  <p>Address: Atlanta, Georgia, USA</p>
                </div>
              </div>
            </div>
          )}

          {/* Terms of Service Content */}
          {activeTab === 'terms' && (
            <div className="space-y-8">
              {/* Agreement to Terms */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Agreement to Terms
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  By accessing or using Alpha Consulting LLC's services, you agree to be bound by these Terms of Service 
                  and all applicable laws and regulations. If you do not agree with any of these terms, you are 
                  prohibited from using or accessing our services.
                </p>
              </div>

              {/* Services Description */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Services Description
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  Alpha Consulting LLC provides software development, consulting, cloud infrastructure, security compliance, 
                  and related technology services. We reserve the right to modify, suspend, or discontinue any 
                  part of our services at any time without prior notice.
                </p>
              </div>

              {/* User Obligations */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  User Obligations
                </h2>
                <div className="text-orange-200 font-nunito space-y-2">
                  <p className="leading-relaxed">You agree to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Provide accurate and complete information when using our services</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Use our services only for lawful purposes</li>
                    <li>Not interfere with or disrupt our services or servers</li>
                    <li>Not attempt to gain unauthorized access to any systems or data</li>
                    <li>Comply with all applicable local, state, national, and international laws</li>
                    <li>Not upload or transmit viruses, malware, or malicious code</li>
                  </ul>
                </div>
              </div>

              {/* Intellectual Property */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Intellectual Property Rights
                </h2>
                <div className="text-orange-200 font-nunito space-y-3">
                  <p className="leading-relaxed">
                    The content, features, and functionality of our website and services are owned by Alpha Consulting LLC 
                    and are protected by international copyright, trademark, patent, trade secret, and other 
                    intellectual property laws.
                  </p>
                  <p className="leading-relaxed">
                    For custom development work, intellectual property ownership will be specified in individual 
                    service agreements. Unless otherwise stated, deliverables created specifically for clients 
                    are typically transferred to the client upon full payment.
                  </p>
                </div>
              </div>

              {/* Payment Terms */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Payment Terms
                </h2>
                <div className="text-orange-200 font-nunito space-y-2">
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>All fees are quoted in USD unless otherwise specified</li>
                    <li>Payment terms will be outlined in individual service agreements</li>
                    <li>Late payments may incur additional fees or service suspension</li>
                    <li>Refund policies vary by service type and will be detailed in contracts</li>
                    <li>You are responsible for all taxes associated with your use of our services</li>
                  </ul>
                </div>
              </div>

              {/* Confidentiality */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Confidentiality
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  We understand the sensitive nature of information shared during engagements. We maintain strict 
                  confidentiality of all client information and typically enter into Non-Disclosure Agreements 
                  (NDAs) before beginning work. Our team members are bound by confidentiality obligations.
                </p>
              </div>

              {/* Warranties and Disclaimers */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Warranties and Disclaimers
                </h2>
                <div className="text-orange-200 font-nunito space-y-3">
                  <p className="leading-relaxed">
                    We strive to provide high-quality services, but our services are provided "as is" without 
                    warranties of any kind, either express or implied. We do not warrant that:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Our services will be uninterrupted, timely, secure, or error-free</li>
                    <li>Results obtained from using our services will be accurate or reliable</li>
                    <li>Any errors will be corrected</li>
                  </ul>
                  <p className="mt-3 leading-relaxed">
                    Specific warranties for deliverables will be outlined in individual service agreements.
                  </p>
                </div>
              </div>

              {/* Limitation of Liability */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Limitation of Liability
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  To the maximum extent permitted by law, Alpha Consulting LLC shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages, including loss of profits, data, use, 
                  or other intangible losses, resulting from your use of our services. Our total liability shall 
                  not exceed the amount paid by you for the specific service giving rise to the claim.
                </p>
              </div>

              {/* Indemnification */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Indemnification
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Alpha Consulting LLC and its officers, directors, 
                  employees, and agents from any claims, liabilities, damages, losses, and expenses arising from 
                  your violation of these Terms or your use of our services.
                </p>
              </div>

              {/* Governing Law */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Governing Law and Jurisdiction
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the State of Georgia, 
                  without regard to its conflict of law provisions. Any disputes arising from these Terms or our 
                  services shall be resolved in the state or federal courts located in Georgia, USA.
                </p>
              </div>

              {/* Dispute Resolution */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Dispute Resolution
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  We encourage clients to contact us first to resolve any disputes informally. If a dispute cannot 
                  be resolved through negotiation, parties may agree to mediation or arbitration before pursuing 
                  litigation. Specific dispute resolution procedures may be outlined in individual service agreements.
                </p>
              </div>

              {/* Termination */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Termination
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  We may terminate or suspend your access to our services immediately, without prior notice, for 
                  conduct that we believe violates these Terms or is harmful to other users, us, or third parties, 
                  or for any other reason. Upon termination, provisions regarding confidentiality, intellectual 
                  property, disclaimers, and limitations of liability shall survive.
                </p>
              </div>

              {/* Changes to Terms */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Changes to Terms
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  We reserve the right to modify these Terms at any time. We will provide notice of significant 
                  changes by posting the updated Terms on our website and updating the "Last updated" date. 
                  Continued use of our services after changes constitutes acceptance of the modified Terms.
                </p>
              </div>

              {/* Contact */}
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h2 className="text-2xl font-semibold font-nunito text-amber-300 mb-4">
                  Contact Information
                </h2>
                <p className="text-orange-200 font-nunito leading-relaxed">
                  For questions about these Terms of Service, please contact us at:
                </p>
                <div className="mt-3 text-orange-200 font-nunito">
                  <p>Email: info@thealphaconsult.com</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PrivacyTermsPage;
